'use client'

import { useRef, useState } from 'react'
import { Search, Sparkles, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { detectBrandFromDomain } from '@/lib/detect-brand-client'
import { RefArrow } from '@/components/reference/tool-live'

interface RedditFormProps {
  onSubmit: (brand: string, subreddits: string, keywords: string) => void
  isLoading: boolean
  /**
   * 'reference' renders the reference tool page's own run form
   * (public/pixelmojo-rebuild/pixelmojo-tools/pixelmojo-tool-reddit-brand-monitor.html
   * `form.run`), so the page's ported stylesheet draws it exactly like the
   * reference. Same state, brand detection, AI Suggest and submit.
   */
  variant?: 'default' | 'reference'
}

/** The reference form's example chip (`data-example`). */
const EXAMPLE_DOMAIN = 'pixelmojo.io'

export default function RedditForm({
  onSubmit,
  isLoading,
  variant = 'default',
}: RedditFormProps) {
  const [brand, setBrand] = useState('')
  const [subreddits, setSubreddits] = useState('')
  const [keywords, setKeywords] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isSuggesting, setIsSuggesting] = useState(false)
  const [domain, setDomain] = useState('')
  const [detecting, setDetecting] = useState(false)

  // Latest field values, read when a detection resolves: a slow answer for
  // a domain the user has since changed (or after they typed a brand) must
  // never overwrite what is on screen now.
  const latest = useRef({ domain: '', brand: '' })
  latest.current = { domain, brand }

  // Auto-fill the brand from a domain so a URL alone is enough to run.
  async function detectFromDomain(value: string) {
    const v = value.trim()
    if (!v) return
    setDetecting(true)
    const d = await detectBrandFromDomain(v)
    if (
      d?.brand &&
      latest.current.domain.trim() === v &&
      !latest.current.brand.trim()
    )
      setBrand(d.brand)
    setDetecting(false)
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    let b = brand.trim()
    // If the user gave a domain but no brand, detect it first.
    if (!b && domain.trim()) {
      setDetecting(true)
      const d = await detectBrandFromDomain(domain)
      setDetecting(false)
      if (d?.brand) {
        b = d.brand
        setBrand(d.brand)
      }
    }
    if (!b) {
      setError('Enter your domain or a brand name to monitor.')
      return
    }
    if (b.length > 100) {
      setError('Brand name must be 100 characters or less.')
      return
    }
    setError(null)
    onSubmit(b, subreddits.trim(), keywords.trim())
  }

  async function handleSuggest() {
    if (!brand.trim()) {
      setError(
        'Enter a brand name first, then we can suggest subreddits and keywords.'
      )
      return
    }

    setIsSuggesting(true)
    setError(null)

    try {
      const res = await fetch('/api/tools/reddit-monitor/suggest', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ brand: brand.trim() }),
      })

      const data = await res.json()

      if (!res.ok) {
        throw new Error(data.message || 'Suggestion failed.')
      }

      if (data.subreddits?.length > 0) {
        setSubreddits(data.subreddits.join(', '))
      }
      if (data.keywords?.length > 0) {
        setKeywords(data.keywords.join(', '))
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Could not generate suggestions.'
      )
    } finally {
      setIsSuggesting(false)
    }
  }

  if (variant === 'reference') {
    const busy = isLoading || isSuggesting
    return (
      <form className='run' noValidate onSubmit={handleSubmit}>
        <label className='run__field'>
          <span>Your domain</span>
          <input
            type='text'
            name='f0'
            value={domain}
            onChange={e => {
              setDomain(e.target.value)
              if (error) setError(null)
            }}
            onBlur={() => detectFromDomain(domain)}
            placeholder='Enter your domain (e.g. notion.so)'
            maxLength={200}
            autoComplete='off'
            inputMode='url'
            disabled={busy}
          />
        </label>
        <label className='run__field'>
          <span>
            Brand
            {detecting ? <i>Detecting…</i> : null}
          </span>
          <input
            type='text'
            name='f1'
            value={brand}
            onChange={e => {
              setBrand(e.target.value)
              if (error) setError(null)
            }}
            placeholder={
              detecting
                ? 'Detecting brand...'
                : 'Brand (auto-detected, edit if needed)'
            }
            autoComplete='off'
            disabled={busy}
          />
        </label>
        <div className='run__row'>
          <label className='run__field'>
            <span>Subreddits</span>
            <input
              type='text'
              name='f2'
              value={subreddits}
              onChange={e => {
                setSubreddits(e.target.value)
                if (error) setError(null)
              }}
              placeholder='Subreddits (e.g. startups, saas, entrepreneur)'
              autoComplete='off'
              disabled={busy}
            />
          </label>
          <label className='run__field'>
            <span>Keywords</span>
            <input
              type='text'
              name='f3'
              value={keywords}
              onChange={e => {
                setKeywords(e.target.value)
                if (error) setError(null)
              }}
              placeholder='Keywords (e.g. pricing, review, alternative)'
              autoComplete='off'
              disabled={busy}
            />
          </label>
        </div>
        <div className='run__actions'>
          <button
            className='btn btn--accent'
            type='submit'
            disabled={busy}
            aria-busy={isLoading || undefined}
          >
            {isLoading ? 'Monitoring…' : 'Monitor Brand'}
            <RefArrow />
          </button>
          <button
            type='button'
            className='btn btn--ghost btn--sm'
            onClick={handleSuggest}
            disabled={busy}
            aria-busy={isSuggesting || undefined}
          >
            {isSuggesting ? 'Suggesting…' : 'AI Suggest'}
          </button>
          <button
            type='button'
            className='btn btn--ghost btn--sm'
            data-example={EXAMPLE_DOMAIN}
            disabled={busy}
            onClick={() => {
              setDomain(EXAMPLE_DOMAIN)
              if (error) setError(null)
              void detectFromDomain(EXAMPLE_DOMAIN)
            }}
          >
            Try {EXAMPLE_DOMAIN}
          </button>
        </div>
        <p className='run__note'>
          <i />
          <span>
            One free audit per email. Verify with a 6-digit code to view it.
          </span>
        </p>
        {error && (
          <p className='run__note run__error' role='alert'>
            <i />
            {error}
          </p>
        )}
      </form>
    )
  }

  return (
    <form onSubmit={handleSubmit} className='w-full max-w-2xl mx-auto'>
      <div className='flex flex-col gap-3'>
        <input
          type='text'
          value={domain}
          onChange={e => {
            setDomain(e.target.value)
            if (error) setError(null)
          }}
          onBlur={() => detectFromDomain(domain)}
          placeholder='Enter your domain (e.g. notion.so)'
          maxLength={200}
          className='w-full h-12 px-4 border border-border rounded-full bg-background text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-primary focus:border-primary transition-colors text-base'
          disabled={isLoading || isSuggesting}
        />
        <div className='relative'>
          <Search
            size={18}
            className='absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground'
          />
          <input
            type='text'
            value={brand}
            onChange={e => {
              setBrand(e.target.value)
              if (error) setError(null)
            }}
            placeholder={
              detecting
                ? 'Detecting brand...'
                : 'Brand (auto-detected, edit if needed)'
            }
            className='w-full h-12 pl-11 pr-4 border border-border rounded-full bg-background text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-primary focus:border-primary transition-colors text-base'
            disabled={isLoading || isSuggesting}
          />
        </div>
        <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
          <input
            type='text'
            value={subreddits}
            onChange={e => {
              setSubreddits(e.target.value)
              if (error) setError(null)
            }}
            placeholder='Subreddits (e.g. startups, saas, entrepreneur)'
            className='w-full h-12 px-4 border border-border rounded-full bg-background text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-primary focus:border-primary transition-colors text-base'
            disabled={isLoading || isSuggesting}
          />
          <input
            type='text'
            value={keywords}
            onChange={e => {
              setKeywords(e.target.value)
              if (error) setError(null)
            }}
            placeholder='Keywords (e.g. pricing, review, alternative)'
            className='w-full h-12 px-4 border border-border rounded-full bg-background text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-primary focus:border-primary transition-colors text-base'
            disabled={isLoading || isSuggesting}
          />
        </div>

        {/* AI Suggest + Submit */}
        <div className='flex items-center justify-center gap-3'>
          <button
            type='button'
            onClick={handleSuggest}
            disabled={isLoading || isSuggesting}
            className='inline-flex items-center gap-1.5 text-xs font-medium text-foreground/60 hover:text-primary transition-colors disabled:opacity-40 disabled:cursor-not-allowed px-3 py-2 rounded-full border border-border/60 hover:border-primary/40'
          >
            {isSuggesting ? (
              <Loader2 size={14} className='animate-spin' />
            ) : (
              <Sparkles size={14} style={{ color: '#8b5cf6' }} />
            )}
            {isSuggesting ? 'Suggesting...' : 'AI Suggest'}
          </button>
          <Button
            type='submit'
            variant='default'
            size='lg'
            loading={isLoading}
            disabled={isLoading || isSuggesting}
            className='h-12 px-8'
          >
            Monitor Brand
          </Button>
        </div>
      </div>
      {error && (
        <p className='text-sm text-[#F90B8A] mt-2 text-center'>{error}</p>
      )}
    </form>
  )
}
